import { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Database,
  Cloud,
  Layers,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  FolderTree,
  Settings,
  Info,
  Calendar,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import api from "@/services/api";

const StatusBadge = ({ status }) => {
  const config = {
    active: { icon: CheckCircle2, label: "Active", className: "bg-green-100 text-green-700 border-green-200" },
    inactive: { icon: AlertCircle, label: "Inactive", className: "bg-gray-100 text-gray-700 border-gray-200" },
    suspended: { icon: AlertCircle, label: "Suspended", className: "bg-red-100 text-red-700 border-red-200" },
  };

  const { icon: Icon, label, className } = config[status] || config.inactive;

  return (
    <Badge variant="outline" className={className}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

// Financial years dropdown options (current year + 10 years back + 5 years forward)
const getFinancialYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear + 5; y >= currentYear - 10; y--) {
    years.push(y.toString());
  }
  return years;
};

const TenantCard = ({ tenant, oracleConnections, bigqueryConnections, onEdit, onDelete, onManageUnits }) => {
  const oracleConn = oracleConnections.find(c => c.id === tenant.oracle_connection_id);
  const bqConn = bigqueryConnections.find(c => c.id === tenant.bigquery_connection_id);

  // Count total schemas across all units
  const totalSchemas = tenant.units?.reduce((sum, unit) => {
    const schemaCount = unit.schemas?.length || (unit.oracle_schema ? 1 : 0);
    return sum + schemaCount;
  }, 0) || 0;

  return (
    <Card className="relative overflow-hidden" data-testid={`tenant-card-${tenant.tenant_id}`}>
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl text-white">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{tenant.name}</CardTitle>
              <CardDescription className="font-mono text-xs mt-0.5">
                {tenant.tenant_id}
              </CardDescription>
            </div>
          </div>
          <StatusBadge status={tenant.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {tenant.description && (
          <p className="text-sm text-muted-foreground">{tenant.description}</p>
        )}

        {/* Connections Info */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Database className="w-4 h-4 text-orange-500" />
            <span className="text-muted-foreground">Oracle:</span>
            {oracleConn ? (
              <span className="font-medium">{oracleConn.name}</span>
            ) : (
              <span className="text-yellow-600 italic">Not configured</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Cloud className="w-4 h-4 text-blue-500" />
            <span className="text-muted-foreground">BigQuery:</span>
            {bqConn ? (
              <span className="font-medium">{bqConn.name}</span>
            ) : (
              <span className="text-yellow-600 italic">Not configured</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <FolderTree className="w-4 h-4 text-green-500" />
            <span className="text-muted-foreground">Bronze Dataset:</span>
            <span className="font-mono text-xs">{tenant.bronze_dataset}</span>
          </div>
        </div>

        {/* Units */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium flex items-center gap-1">
              <Layers className="w-4 h-4" />
              Units ({tenant.units?.length || 0}) • Schemas ({totalSchemas})
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs"
              onClick={() => onManageUnits(tenant)}
            >
              <Settings className="w-3 h-3 mr-1" />
              Manage
            </Button>
          </div>
          {tenant.units && tenant.units.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {tenant.units.map((unit) => (
                <Badge key={unit.unit_id} variant="secondary" className="text-xs">
                  {unit.unit_id} ({unit.schemas?.length || (unit.oracle_schema ? 1 : 0)})
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No units configured</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(tenant)}>
            <Pencil className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive ml-auto">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Tenant</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &quot;{tenant.name}&quot; ({tenant.tenant_id})?
                  This will not delete any data in BigQuery, but will remove the tenant configuration.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(tenant.tenant_id)}
                  className="bg-destructive text-destructive-foreground"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};

// Unit Item with collapsible schemas
const UnitItem = ({ unit, onRemoveUnit, onAddSchema, onRemoveSchema, availableSchemas, schemasLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [schemaForm, setSchemaForm] = useState({
    schema_name: "",
    financial_year: "",
    description: "",
    is_active: true,
  });

  const schemas = unit.schemas || [];
  // Include legacy oracle_schema if present and no schemas array
  const displaySchemas = schemas.length > 0 ? schemas : (unit.oracle_schema ? [{
    schema_name: unit.oracle_schema,
    financial_year: null,
    description: "Legacy schema",
    is_active: true,
  }] : []);

  const handleAddSchema = (e) => {
    e.preventDefault();
    if (!schemaForm.schema_name) {
      toast.error("Schema name is required");
      return;
    }
    onAddSchema(unit.unit_id, schemaForm);
    setSchemaForm({ schema_name: "", financial_year: "", description: "", is_active: true });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg">
      <div className="flex items-center justify-between p-3 bg-muted/50">
        <CollapsibleTrigger className="flex items-center gap-2 hover:text-primary">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div className="text-left">
            <p className="font-medium">{unit.name || unit.unit_id}</p>
            <p className="text-xs text-muted-foreground font-mono">
              ID: {unit.unit_id} • {displaySchemas.length} schema(s)
            </p>
          </div>
        </CollapsibleTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => onRemoveUnit(unit.unit_id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      <CollapsibleContent className="p-3 border-t space-y-4">
        {/* Existing Schemas */}
        <div>
          <h5 className="text-sm font-medium mb-2 flex items-center gap-1">
            <Database className="w-4 h-4" />
            Oracle Schemas
          </h5>
          {displaySchemas.length > 0 ? (
            <div className="space-y-2">
              {displaySchemas.map((schema, idx) => (
                <div key={schema.schema_name || idx} className="flex items-center justify-between p-2 bg-white border rounded">
                  <div className="flex items-center gap-2">
                    <Badge variant={schema.is_active !== false ? "default" : "secondary"} className="text-xs">
                      {schema.schema_name}
                    </Badge>
                    {schema.financial_year && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        FY {schema.financial_year}
                      </span>
                    )}
                    {schema.is_active === false && (
                      <Badge variant="outline" className="text-xs text-yellow-600">Inactive</Badge>
                    )}
                  </div>
                  {schemas.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                      onClick={() => onRemoveSchema(unit.unit_id, schema.schema_name)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic p-2 bg-white border rounded">
              No schemas configured
            </p>
          )}
        </div>

        {/* Add Schema Form */}
        <div className="border-t pt-3">
          <h5 className="text-sm font-medium mb-2">Add Schema</h5>
          <form onSubmit={handleAddSchema} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Schema Name *</Label>
                {schemasLoading ? (
                  <div className="flex items-center gap-2 h-8 px-2 border rounded text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading...
                  </div>
                ) : availableSchemas.length > 0 ? (
                  <Select
                    value={schemaForm.schema_name}
                    onValueChange={(val) => setSchemaForm({ ...schemaForm, schema_name: val })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select schema" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSchemas.map((schema) => (
                        <SelectItem key={schema.schema_name} value={schema.schema_name}>
                          {schema.schema_name} ({schema.table_count} tables)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    className="h-8 text-xs"
                    value={schemaForm.schema_name}
                    onChange={(e) => setSchemaForm({ ...schemaForm, schema_name: e.target.value })}
                    placeholder="e.g., IAS20251"
                  />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Financial Year</Label>
                <Select
                  value={schemaForm.financial_year || "none"}
                  onValueChange={(val) => setSchemaForm({ ...schemaForm, financial_year: val === "none" ? "" : val })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {getFinancialYears().map((year) => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  className="h-8 text-xs"
                  value={schemaForm.description}
                  onChange={(e) => setSchemaForm({ ...schemaForm, description: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="flex items-center gap-2 pt-4">
                <Checkbox
                  id={`active-${unit.unit_id}`}
                  checked={schemaForm.is_active}
                  onCheckedChange={(checked) => setSchemaForm({ ...schemaForm, is_active: checked })}
                />
                <Label htmlFor={`active-${unit.unit_id}`} className="text-xs">Active</Label>
              </div>
            </div>
            <Button type="submit" size="sm" className="w-full h-8">
              <Plus className="w-3 h-3 mr-1" />
              Add Schema
            </Button>
          </form>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [oracleConnections, setOracleConnections] = useState([]);
  const [bigqueryConnections, setBigqueryConnections] = useState([]);
  const [sqlserverConnections, setSqlserverConnections] = useState([]);
  const [postgresqlConnections, setPostgresqlConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tenant form dialog
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantForm, setTenantForm] = useState({
    tenant_id: "",
    name: "",
    description: "",
    source_type: "oracle", // oracle, sqlserver, postgresql
    oracle_connection_id: "",
    sqlserver_connection_id: "",
    postgresql_connection_id: "",
    bigquery_connection_id: "",
    bronze_dataset: "",
  });

  // Units management dialog
  const [unitsDialogOpen, setUnitsDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [unitForm, setUnitForm] = useState({
    unit_id: "",
    name: "",
    description: "",
  });
  const [availableSchemas, setAvailableSchemas] = useState([]);
  const [schemasLoading, setSchemasLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tenantsData, oracleData, sqlserverData, postgresqlData, bqData] = await Promise.all([
        api.getTenants(),
        api.getOracleConnections(),
        api.getSQLServerConnections(),
        api.getPostgreSQLConnections(),
        api.getBigQueryConnections(),
      ]);
      setTenants(tenantsData);
      setOracleConnections(oracleData);
      setSqlserverConnections(sqlserverData);
      setPostgresqlConnections(postgresqlData);
      setBigqueryConnections(bqData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTenantDialog = (tenant = null) => {
    if (tenant) {
      setEditingTenant(tenant);
      setTenantForm({
        tenant_id: tenant.tenant_id,
        name: tenant.name,
        description: tenant.description || "",
        source_type: tenant.source_type || "oracle",
        oracle_connection_id: tenant.oracle_connection_id || "",
        sqlserver_connection_id: tenant.sqlserver_connection_id || "",
        postgresql_connection_id: tenant.postgresql_connection_id || "",
        bigquery_connection_id: tenant.bigquery_connection_id || "",
        bronze_dataset: tenant.bronze_dataset || "",
      });
    } else {
      setEditingTenant(null);
      setTenantForm({
        tenant_id: "",
        name: "",
        description: "",
        source_type: "oracle",
        oracle_connection_id: "",
        sqlserver_connection_id: "",
        postgresql_connection_id: "",
        bigquery_connection_id: "",
        bronze_dataset: "",
      });
    }
    setTenantDialogOpen(true);
  };

  const handleCloseTenantDialog = () => {
    setTenantDialogOpen(false);
    setEditingTenant(null);
    setTenantForm({
      tenant_id: "",
      name: "",
      description: "",
      source_type: "oracle",
      oracle_connection_id: "",
      sqlserver_connection_id: "",
      postgresql_connection_id: "",
      bigquery_connection_id: "",
      bronze_dataset: "",
    });
  };

  const handleSaveTenant = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        await api.updateTenant(editingTenant.tenant_id, tenantForm);
        toast.success("Tenant updated successfully");
      } else {
        await api.createTenant(tenantForm);
        toast.success("Tenant created successfully");
      }
      handleCloseTenantDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save tenant");
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    try {
      await api.deleteTenant(tenantId);
      toast.success("Tenant deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete tenant");
    }
  };

  // Units management
  const handleOpenUnitsDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setUnitsDialogOpen(true);
    setUnitForm({ unit_id: "", name: "", description: "" });
    
    // Load available schemas based on source type
    const sourceType = tenant.source_type || 'oracle';
    const hasConnection = (
      (sourceType === 'oracle' && tenant.oracle_connection_id) ||
      (sourceType === 'sqlserver' && tenant.sqlserver_connection_id) ||
      (sourceType === 'postgresql' && tenant.postgresql_connection_id)
    );
    
    if (hasConnection) {
      setSchemasLoading(true);
      try {
        const data = await api.getTenantSchemas(tenant.tenant_id);
        setAvailableSchemas(data.schemas || []);
      } catch (error) {
        console.error("Failed to load schemas:", error);
        setAvailableSchemas([]);
      } finally {
        setSchemasLoading(false);
      }
    }
  };

  const handleCloseUnitsDialog = () => {
    setUnitsDialogOpen(false);
    setSelectedTenant(null);
    setUnitForm({ unit_id: "", name: "", description: "" });
    setAvailableSchemas([]);
  };

  const handleAddUnit = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    
    try {
      await api.addTenantUnit(selectedTenant.tenant_id, {
        ...unitForm,
        schemas: [], // Initialize with empty schemas array
      });
      toast.success(`Unit "${unitForm.unit_id}" added`);
      setUnitForm({ unit_id: "", name: "", description: "" });
      fetchData();
      // Update selected tenant
      const updated = await api.getTenant(selectedTenant.tenant_id);
      setSelectedTenant(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add unit");
    }
  };

  const handleRemoveUnit = async (unitId) => {
    if (!selectedTenant) return;
    
    try {
      await api.removeTenantUnit(selectedTenant.tenant_id, unitId);
      toast.success(`Unit "${unitId}" removed`);
      fetchData();
      // Update selected tenant
      const updated = await api.getTenant(selectedTenant.tenant_id);
      setSelectedTenant(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove unit");
    }
  };

  const handleAddSchema = async (unitId, schemaData) => {
    if (!selectedTenant) return;
    
    try {
      await api.addUnitSchema(selectedTenant.tenant_id, unitId, schemaData);
      toast.success(`Schema "${schemaData.schema_name}" added to unit`);
      fetchData();
      // Update selected tenant
      const updated = await api.getTenant(selectedTenant.tenant_id);
      setSelectedTenant(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add schema");
    }
  };

  const handleRemoveSchema = async (unitId, schemaName) => {
    if (!selectedTenant) return;
    
    try {
      await api.removeUnitSchema(selectedTenant.tenant_id, unitId, schemaName);
      toast.success(`Schema "${schemaName}" removed`);
      fetchData();
      // Update selected tenant
      const updated = await api.getTenant(selectedTenant.tenant_id);
      setSelectedTenant(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove schema");
    }
  };

  // Auto-generate tenant_id from name
  const handleNameChange = (name) => {
    setTenantForm({
      ...tenantForm,
      name,
      tenant_id: tenantForm.tenant_id === "" || tenantForm.tenant_id === tenantForm.name.replace(/\s+/g, '_')
        ? name.replace(/\s+/g, '_')
        : tenantForm.tenant_id,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tenant Management</h1>
          <p className="text-muted-foreground">
            Manage multi-tenant data lake configurations
          </p>
        </div>
        <Button onClick={() => handleOpenTenantDialog()} data-testid="create-tenant-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Tenant
        </Button>
      </div>

      {/* Architecture Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Info className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h3 className="font-semibold">Multi-Tenant Data Lake Architecture</h3>
              <p className="text-sm text-muted-foreground mt-1">
                <strong>Structure:</strong> Tenant → Units → Schemas (per financial year) •
                <strong> Bronze:</strong> Tenant-isolated datasets •
                <strong> Silver/Gold:</strong> Shared tables with <code className="bg-white px-1 rounded">tenant_id</code> + RLS
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Grid */}
      {tenants.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg">No Tenants</h3>
            <p className="text-muted-foreground text-sm mt-1 text-center max-w-md">
              Create your first tenant to start building your multi-tenant data lake.
              Each tenant represents a separate organization or business unit.
            </p>
            <Button className="mt-4" onClick={() => handleOpenTenantDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Tenant
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              oracleConnections={oracleConnections}
              bigqueryConnections={bigqueryConnections}
              onEdit={handleOpenTenantDialog}
              onDelete={handleDeleteTenant}
              onManageUnits={handleOpenUnitsDialog}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Tenant Dialog */}
      <Dialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTenant ? "Edit Tenant" : "Add New Tenant"}
            </DialogTitle>
            <DialogDescription>
              {editingTenant 
                ? "Update the tenant configuration"
                : "Create a new tenant for your multi-tenant data lake"
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTenant} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tenant Name *</Label>
                <Input
                  value={tenantForm.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Al Rajhi Group"
                  required
                  data-testid="tenant-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tenant ID *</Label>
                <Input
                  value={tenantForm.tenant_id}
                  onChange={(e) => setTenantForm({ ...tenantForm, tenant_id: e.target.value })}
                  placeholder="e.g., Al_Rajhi_Grp"
                  required
                  disabled={!!editingTenant}
                  data-testid="tenant-id-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={tenantForm.description}
                onChange={(e) => setTenantForm({ ...tenantForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>

            <div className="space-y-2">
              <Label>Source Database Type *</Label>
              <Select
                value={tenantForm.source_type}
                onValueChange={(val) => setTenantForm({ 
                  ...tenantForm, 
                  source_type: val,
                  oracle_connection_id: "",
                  sqlserver_connection_id: "",
                  postgresql_connection_id: ""
                })}
              >
                <SelectTrigger data-testid="tenant-source-type-select">
                  <SelectValue placeholder="Select source type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oracle">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      Oracle
                    </span>
                  </SelectItem>
                  <SelectItem value="sqlserver">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      SQL Server
                    </span>
                  </SelectItem>
                  <SelectItem value="postgresql">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      PostgreSQL
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  {tenantForm.source_type === 'oracle' && 'Oracle Connection'}
                  {tenantForm.source_type === 'sqlserver' && 'SQL Server Connection'}
                  {tenantForm.source_type === 'postgresql' && 'PostgreSQL Connection'}
                </Label>
                {tenantForm.source_type === 'oracle' && (
                  <Select
                    value={tenantForm.oracle_connection_id}
                    onValueChange={(val) => setTenantForm({ ...tenantForm, oracle_connection_id: val })}
                  >
                    <SelectTrigger data-testid="tenant-oracle-connection-select">
                      <SelectValue placeholder="Select Oracle" />
                    </SelectTrigger>
                    <SelectContent>
                      {oracleConnections.map((conn) => (
                        <SelectItem key={conn.id} value={conn.id}>
                          {conn.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {tenantForm.source_type === 'sqlserver' && (
                  <Select
                    value={tenantForm.sqlserver_connection_id}
                    onValueChange={(val) => setTenantForm({ ...tenantForm, sqlserver_connection_id: val })}
                  >
                    <SelectTrigger data-testid="tenant-sqlserver-connection-select">
                      <SelectValue placeholder="Select SQL Server" />
                    </SelectTrigger>
                    <SelectContent>
                      {sqlserverConnections.map((conn) => (
                        <SelectItem key={conn.id} value={conn.id}>
                          {conn.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {tenantForm.source_type === 'postgresql' && (
                  <Select
                    value={tenantForm.postgresql_connection_id}
                    onValueChange={(val) => setTenantForm({ ...tenantForm, postgresql_connection_id: val })}
                  >
                    <SelectTrigger data-testid="tenant-postgresql-connection-select">
                      <SelectValue placeholder="Select PostgreSQL" />
                    </SelectTrigger>
                    <SelectContent>
                      {postgresqlConnections.map((conn) => (
                        <SelectItem key={conn.id} value={conn.id}>
                          {conn.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label>BigQuery Connection</Label>
                <Select
                  value={tenantForm.bigquery_connection_id}
                  onValueChange={(val) => setTenantForm({ ...tenantForm, bigquery_connection_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select BigQuery" />
                  </SelectTrigger>
                  <SelectContent>
                    {bigqueryConnections.map((conn) => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bronze Dataset Name</Label>
              <Input
                value={tenantForm.bronze_dataset}
                onChange={(e) => setTenantForm({ ...tenantForm, bronze_dataset: e.target.value })}
                placeholder={`bronze_${tenantForm.tenant_id.toLowerCase().replace(/\s+/g, '_') || 'tenant_id'}`}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to auto-generate from tenant ID
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseTenantDialog}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-tenant-btn">
                {editingTenant ? "Update Tenant" : "Create Tenant"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Units Dialog */}
      <Dialog open={unitsDialogOpen} onOpenChange={setUnitsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Manage Accounting Units - {selectedTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Each unit can have multiple Oracle schemas (e.g., one per financial year).
              Click on a unit to manage its schemas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Existing Units */}
            <div>
              <h4 className="font-medium mb-3">Units ({selectedTenant?.units?.length || 0})</h4>
              {selectedTenant?.units && selectedTenant.units.length > 0 ? (
                <div className="space-y-2">
                  {selectedTenant.units.map((unit) => (
                    <UnitItem
                      key={unit.unit_id}
                      unit={unit}
                      onRemoveUnit={handleRemoveUnit}
                      onAddSchema={handleAddSchema}
                      onRemoveSchema={handleRemoveSchema}
                      availableSchemas={availableSchemas}
                      schemasLoading={schemasLoading}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic p-3 bg-muted rounded-lg">
                  No units configured yet. Add a unit below.
                </p>
              )}
            </div>

            {/* Add New Unit Form */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Add New Unit</h4>
              <form onSubmit={handleAddUnit} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Unit ID *</Label>
                    <Input
                      value={unitForm.unit_id}
                      onChange={(e) => setUnitForm({ ...unitForm, unit_id: e.target.value })}
                      placeholder="e.g., 1 or IND"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit Name *</Label>
                    <Input
                      value={unitForm.name}
                      onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
                      placeholder="e.g., Industrial Division"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={unitForm.description}
                      onChange={(e) => setUnitForm({ ...unitForm, description: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Unit
                </Button>
              </form>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseUnitsDialog}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
